import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building, Layers } from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import MultiStepTenantForm from '../../components/forms/MultiStepTenantForm';
import { getTenant, createTenant, updateTenant } from '../../services/api';
import { TenantFormData, TenantLevel, TenantType } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

interface TenantFormPageProps {
  initialLevel?: TenantLevel;
}

const TenantFormPage: React.FC<TenantFormPageProps> = ({ initialLevel }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [tenant, setTenant] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isEditMode = !!id;
  
  // Get type from URL or default
  const typeFromUrl = searchParams.get('type') || '';
  const levelFromUrl = searchParams.get('level') || '';
  
  // Determine initialType based on initialLevel
  const getInitialType = () => {
    if (initialLevel === TenantLevel.L2 || initialLevel === TenantLevel.L0 || initialLevel === TenantLevel.L1) {
      return TenantType.PARTNER;
    } else if (initialLevel === TenantLevel.L3) {
      return TenantType.ORG;
    }
    
    // Check if we're coming from the partner section in the URL
    if (typeFromUrl === 'partner') {
      return TenantType.PARTNER;
    }
    
    // Default to organization if no level is provided
    return TenantType.ORG;
  };
  
  // Fetch tenant data if in edit mode
  useEffect(() => {
    const fetchTenant = async () => {
      if (!isEditMode) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await getTenant(id!);
        setTenant(data);
      } catch (error: any) {
        console.error('Error fetching tenant:', error);
        let errorMessage = 'Failed to load tenant data. Please try again.';
        
        // Check for specific error messages from the API
        if (error.response) {
          if (error.response.status === 403) {
            errorMessage = 'You do not have permission to access this tenant.';
          } else if (error.response.status === 404) {
            errorMessage = 'Tenant not found. It may have been deleted.';
          } else if (error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTenant();
  }, [id, isEditMode]);
  
  // Handle form submission
  const handleSubmit = async (data: TenantFormData) => {
    // TenantFormPage: handleSubmit called
    setIsSubmitting(true);
    setError(null);
    
    try {
      if (isEditMode) {
        // Update existing tenant
        // TenantFormPage: Updating existing tenant
        await updateTenant(id!, data);
        
        // Redirect based on tenant type after edit
        if (data.tenant_type === 'partner') {
          navigate('/tenants?level=L0,L1,L2&type=partner', { replace: true });
        } else {
          navigate('/tenants?level=L3&type=org', { replace: true });
        }
      } else {
        // Create new tenant with initial level if provided
        if (initialLevel) {
          // TenantFormPage: Setting initial level
          data.level = initialLevel;
        }
        
        const result = await createTenant(data);
        
        // Navigate to appropriate list based on tenant type and level
        if (data.tenant_type === 'partner') {
          navigate('/tenants?level=L0,L1,L2&type=partner', { replace: true });
        } else {
          navigate('/tenants?level=L3&type=org', { replace: true });
        }
      }
    } catch (error: any) {
      console.error('TenantFormPage: Error saving tenant:', error);
      let errorMessage = 'Failed to save tenant. Please try again.';
      
      // Check for specific error messages from the API
      if (error.response && error.response.data) {
        console.error('TenantFormPage: API error response:', error.response.data);
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle going back if there's an error
  const handleBack = () => {
    const typeParam = typeFromUrl;
    const levelParam = levelFromUrl;
    
    if (typeParam === 'partner' || (levelParam && ['L0', 'L1', 'L2'].includes(levelParam))) {
      navigate('/tenants?level=L0,L1,L2&type=partner', { replace: true });
    } else if (typeParam === 'org' || levelParam === 'L3') {
      navigate('/tenants?level=L3&type=org', { replace: true });
    } else {
      navigate('/tenants', { replace: true });
    }
  };
  
  // Determine proper back link URL
  const getBackUrl = () => {
    if (isEditMode) {
      // When editing, check tenant type if available
      if (tenant) {
        return tenant.tenant_type === 'partner' 
          ? '/tenants?level=L0,L1,L2&type=partner' 
          : '/tenants?level=L3&type=org';
      }
      return '/tenants';
    } 
    
    // When creating new, get from URL or use initialLevel
    const typeParam = typeFromUrl;
    
    if (typeParam === 'partner' || initialLevel === 'L0' || initialLevel === 'L1' || initialLevel === 'L2') {
      return '/tenants?level=L0,L1,L2&type=partner';
    } else if (typeParam === 'org' || initialLevel === 'L3') {
      return '/tenants?level=L3&type=org';
    }
    
    return '/tenants';
  };
  
  // Determine page title based on mode and tenant level
  const getPageTitle = () => {
    if (isEditMode) {
      return `Edit ${tenant?.name || 'Tenant'}`;
    }
    
    const typeParam = typeFromUrl;
    const levelParam = levelFromUrl;
    
    if (initialLevel === 'L2' || typeParam === 'partner' || ['L0', 'L1', 'L2'].includes(levelParam)) {
      return 'Add Partner';
    } else if (initialLevel === 'L3' || typeParam === 'org' || levelParam === 'L3') {
      return 'Add Organization';
    }
    
    return 'Add Tenant';
  };
  
  // Determine if this is a partner or organization form
  const isPartnerForm = () => {
    const typeParam = typeFromUrl;
    const levelParam = levelFromUrl;
    
    if (isEditMode && tenant) {
      return tenant.tenant_type === 'partner';
    }
    
    return typeParam === 'partner' || initialLevel === 'L0' || initialLevel === 'L1' || initialLevel === 'L2' || ['L0', 'L1', 'L2'].includes(levelParam);
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Modern header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative z-10">
        <div className="flex items-center gap-3">
          <Link 
            to={getBackUrl()} 
            className="flex items-center justify-center h-10 w-10 rounded-full bg-background-darker hover:bg-primary/10 hover:text-primary transition-colors border border-border-dark"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              {isPartnerForm() ? (
                <Building size={24} className="text-primary/80" />
              ) : (
                <Layers size={24} className="text-primary/80" />
              )}
              {getPageTitle()}
            </h1>
            <p className="text-text-muted-dark mt-1">
              {isEditMode 
                ? "Update tenant information and settings" 
                : isPartnerForm()
                  ? "Create a new partner tenant with multi-level access"
                  : "Create a new organization tenant for client access"
              }
            </p>
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <Card className="relative overflow-hidden">
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-14 h-14 relative">
              <div className="absolute top-0 right-0 h-full w-full rounded-full border-4 border-t-primary border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
              <div className="absolute top-1 right-1 h-12 w-12 rounded-full border-4 border-t-transparent border-r-primary-light border-b-transparent border-l-transparent animate-spin-slow"></div>
            </div>
            <p className="mt-4 text-text-muted-dark animate-pulse">Loading tenant data...</p>
          </div>
        </Card>
      ) : error ? (
        <Card className="p-6 border-red-500/20">
          <div className="flex flex-col items-center text-center">
            <div className="rounded-full bg-red-500/10 p-3 mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-400 mb-2">Error Loading Tenant</h3>
            <p className="text-text-muted-dark mb-4">{error}</p>
            <Button 
              variant="outline" 
              onClick={handleBack}
            >
              Go Back
            </Button>
          </div>
        </Card>
      ) : (
        <MultiStepTenantForm
          tenant={tenant}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          initialLevel={levelFromUrl as TenantLevel || initialLevel}
          initialType={getInitialType()}
        />
      )}
    </div>
  );
};

export default TenantFormPage; 